<script setup>
import { ref, onMounted, computed } from "vue";
import {ref as dbRef, get, child, push} from "firebase/database";
import { database, auth } from "@/firebase";
import { onAuthStateChanged } from "firebase/auth";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonAlert from "@/components/ArgonAlert.vue";
import ArgonRadio from "../components/ArgonRadio.vue";
import router from "../router";

const questions = ref([]);
const currentIndex = ref(0);
const selectedOption = ref("");
const showResult = ref(false);
const resultMessage = ref("");
const correctAnswer = ref("");
const score = ref(0);
const userUid = ref(null);
const finished = ref(false);

const fetchQuestions = async () => {
  const snapshot = await get(child(dbRef(database), "quiz/questions"));
  if (snapshot.exists()) {
    const data = snapshot.val();
    const allQuestions = Object.values(data);
    const shuffled = allQuestions.sort(() => 0.5 - Math.random());
    questions.value = shuffled.slice(0, 10);
  }
};

const currentQuestion = computed(() => questions.value[currentIndex.value]);

const submitAnswer = () => {
  if (!selectedOption.value) return;

  showResult.value = true;
  correctAnswer.value = currentQuestion.value.correct;

  if (selectedOption.value === correctAnswer.value) {
    resultMessage.value = "🎉 Chính xác!";
    score.value++;
  } else {
    resultMessage.value = `❌ Sai rồi. Đáp án đúng là: ${correctAnswer.value}`;
  }
};

const nextQuestion = () => {
  selectedOption.value = "";
  showResult.value = false;

  if (currentIndex.value < questions.value.length - 1) {
    currentIndex.value++;
  } else {
    finished.value = true;
    saveScore();
  }
};

const saveScore = async () => {
  if (!userUid.value) return;
  const resultRef = dbRef(database, `quiz_results/${userUid.value}`);
  await push(resultRef, {
    score: score.value,
    total: questions.value.length,
    createdAt: Date.now()
  });
  await router.push('/quiz');
};

onMounted(() => {
  fetchQuestions();
  onAuthStateChanged(auth, (user) => {
    if (user) {
      userUid.value = user.uid;
    }
  });
});
</script>

<template>
  <div class="container mt-4">
    <div v-if="currentQuestion && !finished">
      <div class="card shadow border-radius-md h-100">
        <div class="card-body d-flex flex-column justify-content-between">
          <div class="d-flex justify-content-between align-items-start">
            <h5 class="card-title">
              Câu {{ currentIndex + 1 }}/{{ questions.length }}:
              <span class="text-warning">{{ currentQuestion.definition.toUpperCase() }}</span> trong tiếng anh là?
            </h5>
          </div>
          <div class="mb-3" v-for="(option, idx) in currentQuestion.options" :key="idx">
            <ArgonRadio
                :value="option"
                :name="'question-' + currentQuestion.id"
                v-model="selectedOption"
                :disabled="showResult"
            >
              {{ option }}
            </ArgonRadio>
          </div>
          <argon-button
              v-if="!showResult"
              @click="submitAnswer"
              color="dark"
              variant="gradient"
              class="mt-3"
          >
            Kiểm tra
          </argon-button>

          <argon-alert v-if="showResult" color="info" class="mt-3" dismissible>
            {{ resultMessage }}
          </argon-alert>

          <argon-button
              v-if="showResult && currentIndex < questions.length - 1"
              @click="nextQuestion"
              color="warning"
              variant="gradient"
              class="mt-3"
          >
            Câu tiếp theo
          </argon-button>
          <argon-button
              v-if="showResult && currentIndex === questions.length - 1 && !finished"
              @click="nextQuestion"
              color="success"
              variant="gradient"
              class="mt-3"
          >
            Kết thúc
          </argon-button>

        </div>
      </div>
    </div>
    <div v-else-if="finished" class="text-center mt-4">
      <argon-alert color="success" class="mb-3">
        Bạn đã hoàn thành! Điểm số: {{ score }} / {{ questions.length }}
      </argon-alert>
    </div>
    <div v-else>
      <p>Đang tải câu hỏi...</p>
    </div>
  </div>
</template>
